// Phase 15 — server-backed notification inbox.
//
// Previously a localStorage stub. Now talks to /api/notifications,
// listens for `notification` socket events for live arrival, and
// broadcasts read/archive state changes across browser tabs via a
// BroadcastChannel so a "mark all read" in one tab clears the bell
// dot in the other.
//
// The hook surface is backwards-compatible: existing call sites still
// see `{ notifications, unreadCount, markAsRead, markAllAsRead,
// markAllRead, clearAll, refresh, push }`. The `push` API is preserved
// for client-only notifications (kept off the server) and falls
// through to a transient client buffer that doesn't sync.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '@/integrations/api/client';
import { getSocket } from '@/hooks/useSocketStatus';

export interface Notification {
  id: string;
  title: string;
  description: string;
  icon: string;
  read: boolean;
  timestamp: Date;
  severity?: 'info' | 'success' | 'warning' | 'critical';
  kind?: string;
  actionHref?: string | null;
  actionLabel?: string | null;
}

interface ServerItem {
  id: string;
  kind: string;
  severity: 'info' | 'success' | 'warning' | 'critical';
  title: string;
  description: string;
  actionHref: string | null;
  actionLabel: string | null;
  createdAt: string;
  archivedAt: string | null;
  read: boolean;
  recipientId: string | null;
}

const CROSS_TAB_CHANNEL = 'prodify:notifications';

function mapServer(s: ServerItem): Notification {
  return {
    id: s.id,
    title: s.title,
    description: s.description,
    icon: s.severity === 'critical' ? 'alert-circle'
        : s.severity === 'warning' ? 'alert-triangle'
        : s.severity === 'success' ? 'check-circle'
        : 'bell',
    read: s.read,
    timestamp: new Date(s.createdAt),
    severity: s.severity,
    kind: s.kind,
    actionHref: s.actionHref,
    actionLabel: s.actionLabel,
  };
}

export function useNotificationCenter() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loaded, setLoaded] = useState(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // ── Fetch the initial snapshot ────────────────────────────────────
  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch('/api/notifications?limit=50') as { items: ServerItem[]; unread: number };
      if (res && Array.isArray(res.items)) {
        setNotifications(res.items.map(mapServer));
        setLoaded(true);
      }
    } catch {
      // Server might not be reachable (or feature disabled) — fall
      // back to a quiet empty list rather than throwing.
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Live updates over socket.io ───────────────────────────────────
  useEffect(() => {
    let socket: ReturnType<typeof getSocket> | null = null;
    try { socket = getSocket(); } catch { /* ignore */ }
    if (!socket) return;
    const onNotif = (data: any) => {
      if (!data || !data.id) return;
      setNotifications((prev) => {
        if (prev.some((p) => p.id === data.id)) return prev;
        const incoming = mapServer({
          id: data.id, kind: data.kind, severity: data.severity, title: data.title,
          description: data.description, actionHref: data.actionHref, actionLabel: data.actionLabel,
          createdAt: data.createdAt || new Date().toISOString(),
          archivedAt: null, read: false, recipientId: data.recipientId ?? null,
        });
        return [incoming, ...prev].slice(0, 50);
      });
    };
    socket.on('notification', onNotif);
    return () => { socket?.off('notification', onNotif); };
  }, []);

  // ── Cross-tab synchronisation ─────────────────────────────────────
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(CROSS_TAB_CHANNEL);
    channelRef.current = ch;
    ch.onmessage = (e) => {
      const msg = e.data;
      if (!msg || !msg.type) return;
      if (msg.type === 'read-one' && msg.id) {
        setNotifications((prev) => prev.map((n) => n.id === msg.id ? { ...n, read: true } : n));
      } else if (msg.type === 'read-all') {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      } else if (msg.type === 'archived' && msg.id) {
        setNotifications((prev) => prev.filter((n) => n.id !== msg.id));
      } else if (msg.type === 'refresh') {
        refresh();
      }
    };
    return () => { ch.close(); channelRef.current = null; };
  }, [refresh]);

  const broadcast = useCallback((msg: { type: string; id?: string }) => {
    try { channelRef.current?.postMessage(msg); } catch {}
  }, []);

  // ── Actions ───────────────────────────────────────────────────────
  const markAsRead = useCallback(async (id: string) => {
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    broadcast({ type: 'read-one', id });
    try { await apiFetch(`/api/notifications/${id}/read`, { method: 'POST' }); }
    catch { /* optimistic — keep UI state */ }
  }, [broadcast]);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    broadcast({ type: 'read-all' });
    try { await apiFetch('/api/notifications/read-all', { method: 'POST' }); }
    catch {}
  }, [broadcast]);

  const archive = useCallback(async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    broadcast({ type: 'archived', id });
    try { await apiFetch(`/api/notifications/${id}/archive`, { method: 'POST' }); }
    catch {}
  }, [broadcast]);

  const clearAll = useCallback(async () => {
    // "Clear all" = archive all currently visible. We do it one at a
    // time to keep the server simple; the user-perceived state flips
    // immediately.
    const snapshot = notifications;
    setNotifications([]);
    broadcast({ type: 'refresh' });
    await Promise.all(snapshot.map((n) => archive(n.id)));
  }, [notifications, archive, broadcast]);

  // Back-compat alias retained for older call sites.
  const markAllRead = markAllAsRead;

  // ── Client-only push (no server round-trip) ───────────────────────
  // Some producers in the SPA (e.g. RealtimeNotificationsProvider for
  // health-check transitions) want to surface a notification without
  // touching the server. We accept those and keep them in-memory only.
  const push = useCallback((n: Omit<Notification, 'read' | 'timestamp'> & { read?: boolean; timestamp?: Date }) => {
    setNotifications((prev) => {
      if (prev.some((p) => p.id === n.id)) return prev;
      return [{
        id: n.id, title: n.title, description: n.description, icon: n.icon,
        read: n.read ?? false, timestamp: n.timestamp ?? new Date(),
        severity: n.severity, kind: n.kind, actionHref: n.actionHref, actionLabel: n.actionLabel,
      }, ...prev].slice(0, 50);
    });
  }, []);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  return {
    notifications,
    unreadCount,
    loaded,
    push,
    markAsRead,
    markAllAsRead,
    markAllRead,
    archive,
    clearAll,
    refresh,
  };
}
